import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, RebateCategory, RebateClaim, RebateClaimStatus, RebateScheme, RebateType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, FileHashField, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip, type FileHashValue } from '../../ui/components';

const ADMIN_ROLES = ['REBATE_ADMIN'];
const CATEGORIES: RebateCategory[] = ['SOLAR_WATER_HEATER', 'EFFICIENT_APPLIANCE', 'EFFICIENT_LIGHTING', 'OTHER'];
const CLAIM_STATUSES: RebateClaimStatus[] = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'DISBURSED', 'WITHDRAWN'];

type Tab = 'schemes' | 'claims';

/** Efficiency/solar-water-heater rebate schemes + claims with OCR-assisted invoice reconciliation and budget-cap enforcement (M35). */
export function Rebates() {
  const { persona } = useAuth();
  const isAdmin = persona?.roles.includes('REBATE_ADMIN') ?? false;
  const [tab, setTab] = useState<Tab>('claims');

  return (
    <div>
      <PageHeader title="Rebate Schemes & Claims" subtitle="Efficiency appliance / solar water heater rebates — invoice reconciliation, budget-cap enforcement, disbursement (M35)." />
      <TabStrip tabs={[{ key: 'claims', label: 'Claims' }, { key: 'schemes', label: 'Schemes' }]} active={tab} onChange={setTab} />
      {!isAdmin ? <RoleNotice roles={ADMIN_ROLES} /> : null}
      {tab === 'schemes' ? <SchemesTab isAdmin={isAdmin} /> : <ClaimsTab isAdmin={isAdmin} />}
    </div>
  );
}

function SchemesTab({ isAdmin }: { isAdmin: boolean }) {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listQ = useApiQuery<Page<RebateScheme>>('/api/v1/m35/rebate-schemes?limit=50');

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {isAdmin ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New rebate scheme
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No rebate schemes" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Category</th>
                <th className="table-head">Budget used</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(s.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{s.code}</td>
                  <td className="table-cell">{s.name}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.category} />
                  </td>
                  <td className="table-cell text-xs tabular-nums">
                    {scr(s.budgetUtilizedScr)} {s.budgetCapTotalScr ? `/ ${scr(s.budgetCapTotalScr)}` : '(no cap)'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={s.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <SchemeDetailModal
          id={selectedId}
          isAdmin={isAdmin}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <CreateSchemeModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SchemeDetailModal({ id, isAdmin, onClose, onChanged }: { id: string; isAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<RebateScheme>(`/api/v1/m35/rebate-schemes/${id}`);
  const s = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m35/rebate-schemes/${id}/${action}`);
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={s ? s.name : 'Rebate scheme'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {s ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Status" value={<StatusBadge status={s.status} />} />
            <Detail label="Rebate" value={s.rebateType === 'FLAT_AMOUNT' ? `${scr(s.rebateAmountScr)} flat` : `${s.rebatePercent}% (max ${scr(s.maxRebateScr)})`} />
            <Detail label="Window" value={`${formatDate(s.windowStart)} → ${s.windowEnd ? formatDate(s.windowEnd) : 'open'}`} />
            <Detail label="Budget" value={`${scr(s.budgetUtilizedScr)} used${s.budgetCapTotalScr ? ` / ${scr(s.budgetCapTotalScr)} cap` : ''}`} />
          </div>
          {s.description ? <p className="text-xs text-slate-500">{s.description}</p> : null}
          {isAdmin ? (
            <div className="flex gap-2">
              {s.status === 'ACTIVE' ? (
                <>
                  <button className="btn-secondary" disabled={busy} onClick={() => act('suspend')}>
                    Suspend
                  </button>
                  <button className="btn-danger" disabled={busy} onClick={() => act('close')}>
                    Close
                  </button>
                </>
              ) : null}
              {s.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => act('reactivate')}>
                  Reactivate
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateSchemeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<RebateCategory>('SOLAR_WATER_HEATER');
  const [rebateType, setRebateType] = useState<RebateType>('FLAT_AMOUNT');
  const [rebateAmountScr, setRebateAmountScr] = useState('');
  const [rebatePercent, setRebatePercent] = useState('');
  const [maxRebateScr, setMaxRebateScr] = useState('');
  const [budgetCapTotalScr, setBudgetCapTotalScr] = useState('');
  const [windowStart, setWindowStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/rebate-schemes', {
        code,
        name,
        category,
        rebateType,
        rebateAmountScr: rebateType === 'FLAT_AMOUNT' ? Number.parseFloat(rebateAmountScr) : undefined,
        rebatePercent: rebateType === 'PERCENTAGE' ? Number.parseFloat(rebatePercent) : undefined,
        maxRebateScr: Number.parseFloat(maxRebateScr),
        budgetCapTotalScr: budgetCapTotalScr ? Number.parseFloat(budgetCapTotalScr) : undefined,
        windowStart,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create scheme');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New rebate scheme" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. SWH_2026" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as RebateCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Rebate type">
            <select className="field-input" value={rebateType} onChange={(e) => setRebateType(e.target.value as RebateType)}>
              <option value="FLAT_AMOUNT">Flat amount</option>
              <option value="PERCENTAGE">Percentage</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {rebateType === 'FLAT_AMOUNT' ? (
            <Field label="Rebate amount (SCR)">
              <input className="field-input" type="number" value={rebateAmountScr} onChange={(e) => setRebateAmountScr(e.target.value)} />
            </Field>
          ) : (
            <Field label="Rebate percent">
              <input className="field-input" type="number" max="100" value={rebatePercent} onChange={(e) => setRebatePercent(e.target.value)} />
            </Field>
          )}
          <Field label="Max rebate (SCR)">
            <input className="field-input" type="number" value={maxRebateScr} onChange={(e) => setMaxRebateScr(e.target.value)} />
          </Field>
          <Field label="Budget cap total (optional)">
            <input className="field-input" type="number" value={budgetCapTotalScr} onChange={(e) => setBudgetCapTotalScr(e.target.value)} />
          </Field>
        </div>
        <Field label="Window start">
          <input className="field-input" type="date" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !code || !name || !maxRebateScr || (rebateType === 'FLAT_AMOUNT' ? !rebateAmountScr : !rebatePercent)}
            onClick={submit}
          >
            {busy ? 'Creating…' : 'Create scheme'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ClaimsTab({ isAdmin }: { isAdmin: boolean }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m35/rebate-claims?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<RebateClaim>>(path);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref, vendor, invoice no…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {CLAIM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
        <button className="btn-secondary" onClick={() => setShowCreate(true)}>
          + New rebate claim
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No rebate claims match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Vendor</th>
                <th className="table-head">Invoice amount</th>
                <th className="table-head">Validation</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.refNo}</td>
                  <td className="table-cell text-xs text-slate-500">{c.vendorName}</td>
                  <td className="table-cell text-xs tabular-nums">{scr(c.invoiceAmountScr)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.validationStatus} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ClaimDetailModal id={selectedId} isAdmin={isAdmin} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateClaimModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ClaimDetailModal({ id, isAdmin, onClose, onChanged }: { id: string; isAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<RebateClaim>(`/api/v1/m35/rebate-claims/${id}`);
  const c = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [disbursementRef, setDisbursementRef] = useState('');
  const [disbursementMethod, setDisbursementMethod] = useState<'BANK_TRANSFER' | 'CHEQUE'>('BANK_TRANSFER');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={c ? c.refNo : 'Rebate claim'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.status} />} />
            <Detail label="Validation" value={<StatusBadge status={c.validationStatus} />} />
            <Detail label="Invoice amount" value={scr(c.invoiceAmountScr)} />
            <Detail label="Vendor" value={c.vendorName} />
            <Detail label="Invoice no / date" value={`${c.invoiceNumber} · ${formatDate(c.invoiceDate)}`} />
            {c.approvedRebateScr ? <Detail label="Approved rebate" value={scr(c.approvedRebateScr)} /> : null}
          </div>
          <p className="text-xs text-slate-500">{c.applianceDescription}</p>
          {c.validationNotes ? <p className="rounded bg-slate-50 p-2 text-xs text-slate-600">Reconciliation: {c.validationNotes}</p> : null}
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.disbursementRef ? <p className="text-xs text-seagreen-600">Disbursed {formatDateTime(c.disbursedAt)} via {c.disbursementMethod} ref {c.disbursementRef}</p> : null}

          {isAdmin && c.status === 'SUBMITTED' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/rebate-claims/${id}/claim-review`))}>
              Claim for review
            </button>
          ) : null}

          {isAdmin && c.status === 'UNDER_REVIEW' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-success" disabled={busy || c.validationStatus === 'REJECTED'} onClick={() => act(() => api.post(`/api/v1/m35/rebate-claims/${id}/approve`))}>
                Approve
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/rebate-claims/${id}/reject`, { reason: rejectReason }))}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {isAdmin && c.status === 'APPROVED' ? (
            <div className="flex gap-2 rounded-lg border border-slate-200 p-3">
              <input className="field-input" placeholder="Disbursement reference" value={disbursementRef} onChange={(e) => setDisbursementRef(e.target.value)} />
              <select className="field-input !w-auto" value={disbursementMethod} onChange={(e) => setDisbursementMethod(e.target.value as never)}>
                <option value="BANK_TRANSFER">Bank transfer</option>
                <option value="CHEQUE">Cheque</option>
              </select>
              <button
                className="btn-primary"
                disabled={busy || !disbursementRef.trim()}
                onClick={() => act(() => api.post(`/api/v1/m35/rebate-claims/${id}/disburse`, { disbursementRef, disbursementMethod }))}
              >
                Record disbursement
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateClaimModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const schemesQ = useApiQuery<Page<RebateScheme>>('/api/v1/m35/rebate-schemes?limit=50&status=ACTIVE');
  const [schemeId, setSchemeId] = useState('');
  const [applianceDescription, setApplianceDescription] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceAmountScr, setInvoiceAmountScr] = useState('');
  const [invoice, setInvoice] = useState<FileHashValue | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!invoice) throw new Error('Invoice photo/scan is required');
      await api.post('/api/v1/m35/rebate-claims', {
        schemeId,
        applianceDescription,
        vendorName,
        invoiceNumber,
        invoiceDate,
        invoiceAmountScr: Number.parseFloat(invoiceAmountScr),
        invoiceDocRef: invoice.docRef,
        invoiceSha256: invoice.sha256,
        invoiceFileName: invoice.fileName,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Failed to submit claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New rebate claim" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          No OCR pipeline exists in this portal (out of scope) — the claim is submitted with just the claimed fields + invoice scan, so it lands as PENDING_OCR and a REBATE_ADMIN reviews it
          manually.
        </p>
        <Field label="Scheme">
          <select className="field-input" value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
            <option value="">Select an active scheme…</option>
            {schemesQ.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Appliance description">
          <input className="field-input" value={applianceDescription} onChange={(e) => setApplianceDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Vendor name">
            <input className="field-input" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
          </Field>
          <Field label="Invoice number">
            <input className="field-input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </Field>
          <Field label="Invoice date">
            <input className="field-input" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Invoice amount (SCR)">
          <input className="field-input" type="number" value={invoiceAmountScr} onChange={(e) => setInvoiceAmountScr(e.target.value)} />
        </Field>
        <FileHashField label="Invoice scan/photo" onChange={setInvoice} required />
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !schemeId || !applianceDescription || !vendorName || !invoiceNumber || !invoiceAmountScr || !invoice}
            onClick={submit}
          >
            {busy ? 'Submitting…' : 'Submit claim'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
